import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/layout/Layout";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Package, ChevronRight, Calendar, Truck } from "lucide-react";
import { canonicalOrderStatus, formatMoney, humanizeStatus } from "../lib/safeData";

const statusColors = {
  pending_payment: "status-pending",
  paid: "status-paid",
  processing: "status-processing",
  shipped: "status-shipped",
  delivered: "status-delivered",
  cancelled: "status-cancelled",
};

export default function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders`, {
        withCredentials: true,
      });
      const data = response.data;
      setOrders(Array.isArray(data?.orders) ? data.orders : Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatPrice = (price, currency) => formatMoney(price, currency);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-8" data-testid="orders-title">
          <Package className="inline h-8 w-8 mr-3" />
          My Orders
        </h1>

        {orders.length > 0 ? (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.order_id}
                className="container-card p-4 cursor-pointer hover:border-slate-400"
                onClick={() => navigate(`/orders/${order.order_id}`)}
                data-testid={`order-${order.order_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="mono text-sm text-slate-500">
                      #{order.order_id}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        {formatDate(order.created_at)}
                      </span>
                    </div>
                  </div>
                  <Badge className={statusColors[canonicalOrderStatus(order.status)] || "bg-slate-200"}>
                    {humanizeStatus(order.status)}
                  </Badge>
                </div>

                <div className="flex items-center gap-4 mb-3">
                  {/* Item Previews */}
                  <div className="flex -space-x-2">
                    {(order.items || []).slice(0, 3).map((item, idx) => (
                      <div
                        key={idx}
                        className="w-10 h-10 bg-slate-100 rounded-sm border-2 border-white overflow-hidden"
                      >
                        {item.images?.[0] ? (
                          <img
                            src={item.images[0]}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package className="w-full h-full p-2 text-slate-300" />
                        )}
                      </div>
                    ))}
                    {order.items?.length > 3 && (
                      <div className="w-10 h-10 bg-slate-200 rounded-sm border-2 border-white flex items-center justify-center text-xs font-medium">
                        +{order.items.length - 3}
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <p className="text-sm text-slate-600">
                      {order.items?.length} item(s)
                    </p>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Truck className="h-3 w-3" />
                      <span className="capitalize">{order.shipping_speed}</span>
                      <span>to {order.destination}</span>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="landed-price text-lg">
                      {formatPrice(order.total, order.currency)}
                    </p>
                  </div>

                  <ChevronRight className="h-5 w-5 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              No orders yet
            </h2>
            <p className="text-slate-600 mb-6">
              Start shopping to place your first order
            </p>
            <Button onClick={() => navigate("/products")} data-testid="browse-products">
              Browse Products
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
